import type { ReactNode } from 'react';

/**
 * A toggleable facet chip.
 *
 * `role="checkbox"` rather than a button: several chips in a facet can be on at
 * once, and a screen reader needs to hear the pressed state, not just the word.
 *
 * A chip whose count is zero is DISABLED, never hidden. Hiding it takes the
 * affordance away with it — the reader can no longer see that the option exists
 * and that some other selection would bring it back.
 */
export interface FilterChipProps {
  label: ReactNode;
  active: boolean;
  onToggle: () => void;
  /** Rendered as a trailing tally. Zero disables the chip. */
  count?: number;
  /** A severity or state dot — always accompanied by the label, never alone. */
  dot?: string;
  title?: string;
  disabled?: boolean;
}

export default function FilterChip({
  label,
  active,
  onToggle,
  count,
  dot,
  title,
  disabled,
}: FilterChipProps) {
  // An ACTIVE chip is never disabled, whatever its count. A selected filter
  // that has narrowed its own count to zero still has to be clickable — it is
  // the only control that can undo itself.
  const isDisabled = !active && (disabled || count === 0);

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      title={title}
      disabled={isDisabled}
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:opacity-45 ${
        active
          ? 'border-primary-500 bg-primary-50 font-semibold text-primary-700'
          : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-neutral-900'
      }`}
    >
      {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />}
      {label}
      {count != null && (
        <span
          className={`font-mono tabular-nums ${
            active ? 'text-primary-500' : 'text-neutral-400'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
